import React from 'react'
import Button from '../../../../../../components/ui/Button'
import { FiClock, FiDollarSign, FiEdit2, FiEye, FiEyeOff, FiImage, FiStar, FiTag, FiTrash2 } from 'react-icons/fi'
import Badge from '../../../../../../components/ui/Badge'
import { motion } from 'framer-motion'
import { MenuItem } from '../../../../../../utils/supabaseClient'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectCategories, selectCurrentBranch } from '../../../../../../store/selectors'
import { getCategoryName } from '../../../../../../utils/categoryHelper'

interface HeroSectionProps {
	cardVariants: {
		hidden: {
			opacity: number;
			y: number;
		};
		visible: {
			opacity: number;
			y: number;
		};
	},
	menuItem: MenuItem,
	itemName: string,
	hasTranslation: boolean,
	setDisplayLanguage: React.Dispatch<React.SetStateAction<"en" | "ar">>,
	displayLanguage: "en" | "ar",
	togglePublishStatus: () => Promise<void>,
	setIsUpdateModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
	cardVariants, menuItem, itemName, 
	hasTranslation, displayLanguage, setDisplayLanguage,
	togglePublishStatus, setIsUpdateModalOpen, setIsDeleteModalOpen
}) => {
	const { t, i18n } = useTranslation()
	const branch = useAppSelector(selectCurrentBranch)
	const categories = useAppSelector(selectCategories)

	const getCategoryNameById = (categoryId: string | null) => {
		if (!categoryId) return t('menuItems.noCategory');
		const cat = categories.find(cat => cat.id === categoryId);
		return cat ? getCategoryName(cat, i18n.language) : t('menuItems.unknownCategory');
	};

	return (
		<motion.div variants={cardVariants} className="mb-6 sm:mb-8">
			{/* <Card className="overflow-hidden border-none shadow-lg"> */}
			<div className="relative overflow-hidden rounded-xl shadow-lg">
				{/* Background Image or Gradient */}
				{menuItem.image_url ? (
					<div className="relative h-64 sm:h-80 overflow-hidden">
						<img
							src={menuItem.image_url}
							alt={itemName}
							className="w-full h-full object-cover"
						/>
						<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
					</div>
				) : (
					branch?.default_item_image ? (
						<div className="relative h-64 sm:h-80 overflow-hidden">
							<img
								src={branch?.default_item_image}
								alt={itemName}
								className="w-full h-full object-cover"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
						</div>
					) : (
						<div className="relative h-64 sm:h-80 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
							<FiImage className="w-16 h-16 sm:w-24 sm:h-24 text-gray-400" />
							<div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
						</div>
					)
				)}

				{/* Content Overlay */}
				<div className="absolute bottom-0 start-0 end-0 p-4 sm:p-6 lg:p-8">
					<div className="flex flex-col gap-4 sm:gap-6">
						<div className="flex-1">
							{hasTranslation && (
								<div className="mb-4">
									<div className="inline-flex rounded-full bg-white/20 p-1 mb-4">
										<button
											onClick={() => setDisplayLanguage('en')}
											className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full ${displayLanguage === 'en'
												? 'bg-white text-gray-900'
												: 'text-white hover:bg-white/10'
												} transition-colors`}
										>
											English
										</button>
										<button
											onClick={() => setDisplayLanguage('ar')}
											className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full ${displayLanguage === 'ar'
												? 'bg-white text-gray-900'
												: 'text-white hover:bg-white/10'
												} transition-colors`}
										>
											العربية
										</button>
									</div>
								</div>
							)}

							<div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
								{menuItem.is_published ? (
									<Badge variant="success" className="bg-green-500/20 border-green-400/30 text-green-100">
										<FiEye className="w-3 h-3 me-1" />
										{t('menuItems.published')}
									</Badge>
								) : (
									<Badge variant="gray" className="bg-gray-500/20 border-gray-400/30 text-gray-100">
										<FiEyeOff className="w-3 h-3 me-1" />
										{t('menuItems.draft')}
									</Badge>
								)}
								{!menuItem.is_available && (
									<Badge variant="error" className="bg-red-500/20 border-red-400/30 text-red-100">
										{t('menuItems.unavailable')}
									</Badge>
								)}
								{menuItem.is_featured && (
									<Badge variant="warning" className="bg-amber-500/20 border-amber-400/30 text-amber-100">
										<FiStar className="w-3 h-3 me-1" />
										{t('menuItems.featured')}
									</Badge>
								)}
							</div>

							<h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3" dir={displayLanguage === 'ar' ? 'rtl' : 'ltr'}>
								{itemName}
							</h1>

							<div className="flex flex-wrap items-center gap-3 sm:gap-6 text-gray-200 text-sm sm:text-base">
								<span className="flex items-center gap-2">
									<FiTag className="w-4 h-4" />
									<span className="truncate">{getCategoryNameById(menuItem.category_id)}</span>
								</span>
								{menuItem.preparation_time && (
									<span className="flex items-center gap-2">
										<FiClock className="w-4 h-4" />
										{menuItem.preparation_time} {t('menuItems.minutes')}
									</span>
								)}
								<span className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-green-400">
									<FiDollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
									{menuItem.price?.toFixed(2)}
								</span>
							</div>
						</div>

						{/* Action Buttons - Hidden on mobile, will be in tabs */}
						<div className="hidden sm:flex gap-3 flex-wrap">
							<Button
								variant="outline"
								className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
								leftIcon={menuItem.is_published ? <FiEyeOff /> : <FiEye />}
								onClick={togglePublishStatus}
								size="sm"
							>
								<span className="hidden lg:inline">
									{menuItem.is_published ? t('menuItems.unpublish') : t('menuItems.publish')}
								</span>
							</Button>

							<Button
								className="bg-primary hover:bg-primary-dark"
								leftIcon={<FiEdit2 />}
								onClick={() => setIsUpdateModalOpen(true)}
								size="sm"
							>
								<span className="hidden lg:inline">{t('common.edit')}</span>
							</Button>

							<Button
								variant="outline"
								className="bg-red-500/10 border-red-400/20 text-red-400 hover:bg-red-500/20"
								leftIcon={<FiTrash2 />}
								onClick={() => setIsDeleteModalOpen(true)}
								size="sm"
							>
								<span className="hidden lg:inline">{t('common.delete')}</span>
							</Button>
						</div>
					</div>
				</div>
			</div>
			{/* </Card> */}
		</motion.div>
	)
}

export default HeroSection